/**
 * O QUE O `throw` ENTREGOU — normalizado, porque o tipo MENTIA.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ESTE ARQUIVO EXISTE: o limite de erro reproduzia o defeito que ele
 * existe para matar.
 *
 * `ModuleErrorBoundary` declarava:
 *
 *     static getDerivedStateFromError(erro: Error): State { return { erro }; }
 *     interface State { erro: Error | null }
 *     if (erro === null) return this.props.children;
 *     <code>{erro.message}</code>
 *
 * **React entrega `unknown`, não `Error`.** `throw` aceita qualquer valor, e
 * `undefined` é um valor. Com `throw undefined`:
 *
 *  1. `getDerivedStateFromError(undefined)` põe `erro: undefined` no estado —
 *     `tsc` limpo, porque a assinatura promete `Error`;
 *  2. `erro === null` é **falso** (`undefined !== null`), então o boundary
 *     decide RENDERIZAR a tela de falha;
 *  3. `erro.message` estoura **dentro do `render` do próprio boundary**.
 *
 * Um erro lançado no `render` de um boundary não é capturado por ele mesmo. O
 * React desmonta a árvore INTEIRA. Medido no navegador:
 *
 *     document.body.innerText.length === 0
 *     document.getElementById('root').childElementCount === 0
 *     zero links de menu
 *     Uncaught undefined
 *     The above error occurred in the <ModuleErrorBoundary> component
 *     TypeError: Cannot read properties of undefined (reading 'message')
 *
 * É o portal em branco de volta — **por dentro da peça escrita para
 * impedi-lo** —, e a única diferença em relação ao caso que funciona é o TIPO
 * do valor lançado. `throw new TypeError(...)` isolava a tela; `throw undefined`
 * derrubava o portal.
 *
 * ---------------------------------------------------------------------------
 * O QUE ESTA FUNÇÃO GARANTE, e por que é uma função e não um `?.`
 *
 * `erro?.message ?? 'erro'` resolveria o estouro e traria de volta o defeito de
 * VOCABULÁRIO que esta fase persegue: "erro" é o "algo deu errado" que o
 * cabeçalho de `ModuleErrorBoundary` proíbe. Um `throw undefined` num portal de
 * operação tem uma causa NOMEÁVEL — alguém lançou um valor que não é erro — e é
 * isso que vai para a tela e para o `console.error` de quem diagnostica.
 *
 * A função devolve SEMPRE texto não vazio. Isto é invariante de teste, varrido
 * sobre uma lista de valores lançáveis que inclui os cinco que derrubavam o
 * portal (`undefined`, `null`, `0`, `''`, `{}`) e os dois que derrubam
 * `String()` (símbolo e objeto com `toString` que lança).
 */

export interface LancadoNormalizado {
  /**
   * O texto que vai para a tela. NUNCA vazio — é o que o `<code>` imprime, e um
   * `<code>` vazio é um retângulo sem informação no lugar da causa.
   */
  readonly texto: string;
  /**
   * O valor lançado era um `Error`? `false` é informação de diagnóstico, não
   * detalhe: `throw undefined` num `catch` mal escrito e um `TypeError` real
   * pedem investigações diferentes, e a tela de falha é o único lugar onde o
   * operador consegue reportar a diferença.
   */
  readonly foiError: boolean;
}

/**
 * A REDE DE ÚLTIMO RECURSO, nomeada e exportada — para o TESTE poder proibi-la.
 *
 * Ver o `try/catch` externo de `descricaoDoLancado`. Esta string aparecer na
 * tela é uma falha do normalizador, não um sucesso da rede: há teste varrendo o
 * espaço de objetos hostis e reprovando qualquer valor que chegue até aqui.
 */
export const NAO_FOI_POSSIVEL_DESCREVER =
  'o valor lançado resistiu a toda tentativa de descrição — nem o tipo dele pôde ser lido';

/**
 * `x instanceof Error` PODE LANÇAR, e esta foi a linha que reabriu o portal em
 * branco.
 *
 * ---------------------------------------------------------------------------
 * O DEFEITO, medido no navegador.
 *
 * `instanceof` invoca `[[GetPrototypeOf]]` no operando da esquerda, e um `Proxy`
 * pode fazer isso lançar:
 *
 *     throw new Proxy({}, { getPrototypeOf() { throw new Error('gpo') } })
 *
 *     bodyInnerTextLength: 0      rootChildElementCount: 0      menu: 0
 *     Uncaught Error: gpo
 *         at [Symbol.hasInstance]
 *         at descricaoDoLancado
 *         at RootErrorBoundary.render
 *
 * `String()` estava protegido por `textoSeguro` e a leitura de propriedade por
 * `propriedadeSegura`. O `instanceof` ficou NU — era a única operação do arquivo
 * fora de `try/catch`, e lançava DENTRO do `render` do boundary, que é o
 * mecanismo que o cabeçalho deste arquivo descreve para `erro.message`.
 *
 * `Object.prototype.toString.call` é o plano B e TAMBÉM vai dentro de `try`: ele
 * lê `Symbol.toStringTag`, que passa pelo trap `get`.
 */
function ehErro(valor: unknown): boolean {
  try {
    if (valor instanceof Error) return true;
  } catch {
    /* segue para o plano B */
  }
  try {
    return Object.prototype.toString.call(valor) === '[object Error]';
  } catch {
    /* Não dá para saber. `false` é a resposta HONESTA: o texto vai dizer que o
       valor não era um `Error`, e "não era" é melhor que afirmar que era. */
    return false;
  }
}

/** `String(x)` pode LANÇAR — símbolo, e objeto com `toString` que lança. */
function textoSeguro(valor: unknown): string | null {
  try {
    const s = String(valor);
    return typeof s === 'string' && s.length > 0 ? s : null;
  } catch {
    return null;
  }
}

/**
 * LER UMA PROPRIEDADE PODE LANÇAR — e esta foi pega pelo próprio teste.
 *
 * `erro.message` sobre `{ get message() { throw ... } }` estoura no ACESSO, não
 * na leitura de `undefined`. Não é hipótese de laboratório: é a forma de
 * qualquer objeto com `Proxy` ou getter derivado, e um normalizador que estoura
 * ao normalizar é o defeito original com outra pele — o erro sai de dentro da
 * peça que existe para conter erro.
 */
function propriedadeSegura(valor: object, nome: string): unknown {
  try {
    return (valor as Record<string, unknown>)[nome];
  } catch {
    return undefined;
  }
}

/**
 * `typeof` do valor, para a frase nomear o que chegou em vez de suavizar.
 *
 * `Array.isArray` DENTRO DE `try`, e esta foi achada pela varredura gerada: ela
 * LANÇA sobre `Proxy` revogado, porque precisa alcançar o alvo para decidir. Foi
 * a segunda operação nua do arquivo, e só apareceu porque a lista de 31 valores
 * virou um espaço gerado — `Proxy.revocable` não estava, e nunca estaria, numa
 * lista escrita à mão.
 *
 * `typeof` nunca lança, nem sobre `Proxy` revogado, e vale registrar quais
 * operações dispensam rede. A redação anterior dizia que `typeof` era a ÚNICA, e
 * isso era FALSO: `===` também não lança (comparação estrita não invoca trap
 * nenhum), e `Array.isArray` lança mesmo sendo primitiva de linguagem. A lista
 * certa é: `typeof`, `===` e `!==`. Tudo o mais vai dentro de `try`.
 */
function tipoLegivel(valor: unknown): string {
  try {
    if (Array.isArray(valor)) return 'um array';
  } catch {
    /* `Proxy` revogado. `typeof` abaixo ainda responde 'object'. */
  }
  const t = typeof valor;
  if (t === 'object') return 'um objeto';
  if (t === 'function') return 'uma função';
  if (t === 'symbol') return 'um símbolo';
  if (t === 'bigint') return 'um bigint';
  if (t === 'boolean') return 'um booleano';
  if (t === 'number') return 'um número';
  if (t === 'string') return 'uma string';
  return `um valor do tipo ${t}`;
}

/**
 * O CORPO. A rede externa está em `descricaoDoLancado`, logo abaixo.
 *
 * Separado para que o `try/catch` externo envolva TUDO sem um `return` escapar
 * por dentro dele — e para que a rede não possa ser confundida com um ramo do
 * algoritmo.
 */
function descrever(erro: unknown): LancadoNormalizado {
  /* `erro == null` e não `=== null`: cobre `undefined` E `null` na mesma
     comparação, e são os dois valores que a sentinela `=== null` confundia com
     "não houve erro". A distinção entre eles é mantida no TEXTO, porque
     `throw null` e `throw undefined` vêm de códigos diferentes. */
  if (erro === undefined) {
    return { texto: 'o código lançou `undefined` — não houve mensagem de erro', foiError: false };
  }
  if (erro === null) {
    return { texto: 'o código lançou `null` — não houve mensagem de erro', foiError: false };
  }

  if (ehErro(erro)) {
    /* `Error` com `message` vazia existe (`new Error()`), e imprimir vazio é o
       mesmo retângulo sem informação. O `name` sempre tem algo. */
    const cru = propriedadeSegura(erro as object, 'message');
    const msg = typeof cru === 'string' ? cru.trim() : '';
    if (msg.length > 0) return { texto: msg, foiError: true };
    const cruNome = propriedadeSegura(erro as object, 'name');
    const nome = typeof cruNome === 'string' && cruNome.length > 0 ? cruNome : 'Error';
    return { texto: `${nome} sem mensagem`, foiError: true };
  }

  /* Objeto que PARECE erro (vem de outro realm, de uma lib, de um `reject`) e
     traz mensagem utilizável: usa a mensagem e declara que não era `Error`. */
  if (typeof erro === 'object') {
    const talvez = propriedadeSegura(erro, 'message');
    if (typeof talvez === 'string' && talvez.trim().length > 0) {
      return { texto: talvez.trim(), foiError: false };
    }
  }

  if (typeof erro === 'string') {
    const s = erro.trim();
    return s.length > 0
      ? { texto: s, foiError: false }
      : { texto: 'o código lançou uma string vazia — não houve mensagem de erro', foiError: false };
  }

  const bruto = textoSeguro(erro);
  /* `"[object Object]"` não é informação; dizer que veio um objeto sem mensagem
     é. O mesmo vale para o que `String()` não consegue converter. */
  if (bruto === null || bruto === '[object Object]') {
    return {
      texto: `o código lançou ${tipoLegivel(erro)} sem mensagem de erro`,
      foiError: false,
    };
  }
  return { texto: `o código lançou ${tipoLegivel(erro)}: ${bruto}`, foiError: false };
}

/**
 * A FUNÇÃO PÚBLICA — e o `try/catch` externo é ESTRUTURAL, não desculpa.
 *
 * ---------------------------------------------------------------------------
 * POR QUE A REDE EXISTE, depois de a operação nua já ter sido consertada.
 *
 * O invariante deste módulo é "**nunca lança**", e ele é a ÚLTIMA linha de
 * defesa: quando ele falha, não há mais nada — o erro sai do `render` do
 * boundary e o React desmonta a árvore inteira.
 *
 * Consertar o `instanceof` fecha o defeito conhecido. A rede fecha a CLASSE:
 * qualquer operação adicionada a `descrever` no futuro herda a garantia, em vez
 * de ela depender de alguém lembrar de embrulhar a próxima. É a mesma diferença
 * entre "não escreva assim" e "não há o que escrever" que as guardas de texto
 * deste projeto perseguem — aqui aplicada ao próprio algoritmo.
 *
 * E A REDE NÃO PODE VIRAR O CAMINHO NORMAL: `NAO_FOI_POSSIVEL_DESCREVER` é
 * exportado e o teste PROÍBE que qualquer valor do espaço varrido chegue nele.
 * Sem isso, a rede teria feito o defeito do `instanceof` passar verde com a
 * mensagem degradada — que é exatamente o tipo de conserto que esconde o
 * próximo.
 */
export function descricaoDoLancado(erro: unknown): LancadoNormalizado {
  try {
    const saida = descrever(erro);
    /* `descrever` pode, em tese, devolver algo estranho se alguém mexer nela: o
       invariante de texto NÃO VAZIO é reafirmado aqui, onde é barato. */
    if (typeof saida?.texto === 'string' && saida.texto.trim().length > 0) return saida;
  } catch {
    /* cai na rede */
  }
  return { texto: NAO_FOI_POSSIVEL_DESCREVER, foiError: false };
}
